import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Erro vindo do servidor/proxy (5xx, Cloudflare 52x, HTML, timeout ou rede) e não do usuário
export const isErroServidor = (error) => {
  if (!error.response) return true;
  const status = error.response.status;
  const data = error.response.data;
  if (status >= 500) return true;
  return typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html') || data.includes('Cloudflare'));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [senhaExpirada, setSenhaExpirada] = useState(false);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setSenhaExpirada(false);
    localStorage.removeItem('token');
    localStorage.removeItem('lastLoginDate');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Verificar logout automático todo domingo à meia-noite
  useEffect(() => {
    if (!token) return;

    const checkDomingo = () => {
      const now = new Date();
      // 0 = Domingo
      if (now.getDay() === 0 && now.getHours() === 0 && now.getMinutes() < 2) {
        console.log('Logout automático: domingo à meia-noite');
        logout();
        alert('Sessão semanal encerrada (domingo 00:00). Por favor, faça login novamente.');
        window.location.href = '/login';
      }
    };

    // Verificar a cada minuto
    const interval = setInterval(checkDomingo, 60000);

    // Verificar imediatamente ao carregar
    checkDomingo();

    return () => clearInterval(interval);
  }, [token, logout]);

  // Configurar axios com token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Check if user has expired password
      if (response.data.senha_expirada) {
        setSenhaExpirada(true);
      }
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
      // Só deslogar se for 401 (token inválido/expirado)
      // Não deslogar para 403 (proxy/permissão) ou erros de rede
      const status = error.response?.status;
      if (status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, senha) => {
    const payload = { email, senha };
    const MAX_TENTATIVAS = 3;
    let error = null;

    for (let tentativa = 1; tentativa <= MAX_TENTATIVAS; tentativa++) {
      try {
        const response = await axios.post(`${API}/auth/login`, payload, { timeout: 20000 });
        const { access_token, user: userData } = response.data;

        setToken(access_token);
        setUser(userData);
        localStorage.setItem('token', access_token);
        localStorage.setItem('lastLoginDate', new Date().toDateString());
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        if (response.data.senha_expirada) {
          setSenhaExpirada(true);
          return { success: true, senha_expirada: true };
        }
        return { success: true };
      } catch (err) {
        error = err;
        // Só repete quando o servidor/proxy falhou (5xx, Cloudflare 52x, timeout, rede)
        if (!isErroServidor(err) || tentativa === MAX_TENTATIVAS) break;
        await new Promise((r) => setTimeout(r, 1500 * tentativa));
      }
    }

    const data = error.response?.data;
    let message = 'Erro ao fazer login';
    if (isErroServidor(error)) {
      message = error.response
        ? 'Servidor temporariamente indisponível. Aguarde alguns segundos e tente novamente.'
        : 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    } else if (typeof data === 'object' && data?.detail) {
      message = typeof data.detail === 'string' ? data.detail : 'Dados de login inválidos';
    }
    return { success: false, message };
  };

  const clearSenhaExpirada = () => setSenhaExpirada(false);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, senhaExpirada, clearSenhaExpirada }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
